
import React from 'react';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Check, X } from 'lucide-react';

export interface Referral {
  id: string;
  name: string;
  email: string;
  status: 'pending' | 'approved' | 'rejected';
  date: string;
}

interface ReferralListProps {
  referrals: Referral[];
  isAdmin?: boolean;
  onApprove?: (id: string) => void;
  onReject?: (id: string) => void;
}

const ReferralList: React.FC<ReferralListProps> = ({ 
  referrals, 
  isAdmin = false,
  onApprove,
  onReject
}) => {
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <Badge variant="outline" className="bg-yellow-100 text-yellow-800 border-yellow-200">Pending</Badge>;
      case 'approved':
        return <Badge variant="outline" className="bg-green-100 text-green-800 border-green-200">Approved</Badge>;
      case 'rejected':
        return <Badge variant="outline" className="bg-red-100 text-red-800 border-red-200">Rejected</Badge>;
      default:
        return <Badge variant="outline">Unknown</Badge>;
    }
  };

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Name</TableHead>
          <TableHead>Email</TableHead>
          <TableHead>Date</TableHead>
          <TableHead>Status</TableHead>
          {isAdmin && <TableHead>Actions</TableHead>}
        </TableRow>
      </TableHeader>
      <TableBody>
        {referrals.length === 0 ? (
          <TableRow>
            <TableCell colSpan={isAdmin ? 5 : 4} className="text-center py-6">
              No referrals found
            </TableCell>
          </TableRow>
        ) : (
          referrals.map((referral) => (
            <TableRow key={referral.id}>
              <TableCell className="font-medium">{referral.name}</TableCell>
              <TableCell>{referral.email}</TableCell>
              <TableCell>{referral.date}</TableCell>
              <TableCell>{getStatusBadge(referral.status)}</TableCell>
              {isAdmin && referral.status === 'pending' && (
                <TableCell>
                  <div className="flex space-x-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="bg-green-50 text-green-600 hover:bg-green-100 border-green-200"
                      onClick={() => onApprove && onApprove(referral.id)}
                    >
                      <Check className="h-4 w-4 mr-1" /> Approve
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="bg-red-50 text-red-600 hover:bg-red-100 border-red-200"
                      onClick={() => onReject && onReject(referral.id)}
                    >
                      <X className="h-4 w-4 mr-1" /> Reject
                    </Button>
                  </div>
                </TableCell>
              )}
              {isAdmin && referral.status !== 'pending' && (
                <TableCell>
                  <span className="text-sm text-gray-500 italic">Processed</span>
                </TableCell>
              )}
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  );
};

export default ReferralList;
